import { PropType, reactive, ref } from 'vue';
import BasePagedListSetupService from '@/setup/BasePagedListSetupService';
import useCandidateService from '@/services/CandidateService';
import TempWorkerListItemEntity from '@/entity/temp-worker/TempWorkerListItemEntity';
import type OrderEntity from '@/entity/order/OrderEntity';
import useOrderService from '@/services/OrderService';
import useTempWorkerPagedListFilterService from '@/services/temp-worker/TempWorkerPagedListFilterService';
import { Bus } from '@orion.ui/orion';

type Props = ServiceProps<typeof TempWorkerPagedListViewSetupService.props>

export default class TempWorkerPagedListViewSetupService
	extends BasePagedListSetupService <Props, () => typeof TempWorkerListItemEntity> {
	static readonly props = {
		inModal: Boolean,
		filter: {
			type: Object,
			default: undefined,
		},
		order: {
			type: Object as PropType<OrderEntity>,
			default: undefined,
		},
		listFooterActions: {
			type: Array as PropType<string[]>,
			default: () => ['add'],
		},
		filterProps: {
			type: Object,
			default: undefined,
		},
	};

	override get useLayoutAsideForDetails () { return false; }

	protected get listItemEntity () {
		if (this.filterService.isInOrderContext) {
			return (entries: IOrderUserAgencyListItemModel[]) => {
				return entries
					.filter(x => !!x.user)
					.map(x => new TempWorkerListItemEntity(x.user, x)) as TempWorkerListItemEntity[];
			};
		} else {
			return (entries: Armado.TempWorkerListItemEntityType[]) => {
				return TempWorkerListItemEntity.map(entries) as TempWorkerListItemEntity[];
			};
		}
	};

	private readonly listPrivateState = reactive({
		isMounted: false,
		selectedWithNoAvailabilities: [],
	});

	readonly _pageSubactions = ref<HTMLElement>();
	private _modal?: OrionModal;

	protected listBind: ListBind = {
		itemType: 'Intérimaire',
		layout: 'grid',
		cellClass: 'cell',
		gridClass: 'arm-grid',
	};

	filterService = useTempWorkerPagedListFilterService();
	candidateService = useCandidateService();
	orderService = useOrderService();

	get permissionGuard (): PermissionGuard { return 'TempWorkerUserRead'; }
	get listTitle () { return `Intérimaire`.pluralize(this.listTotal); }
	get filter () { return this.filterService.filter; }
	get isMounted () { return this.listPrivateState.isMounted; }
	get slotName () { return this.candidateService.inOrder() ? 'actions' : 'subactions'; }

	get canAddCandidate () {
		return (this.candidateService.inOrder() && this.props.order?.canModify())
			|| (this.candidateService.inTalentPools() && this.props.listFooterActions.includes('add'));
	}

	get canRemoveCandidate () {
		return (this.candidateService.inOrder() && this.props.order?.canModify())
			|| this.candidateService.inTalentPools() && this.props.listFooterActions.includes('remove');
	}

	get selectedWithNoAvailabilities () {
		if (!this.listState.selected) return [];
		return this.listState.selected.filter(x => x.availabilityDate === null,
		);
	}

	constructor (props: Props, _modal?: OrionModal) {
		super(props, _modal);
		this._modal = _modal;
	}

	protected async onBeforeMountAsync () {
		this.setFilterServiceAdditionalDatas();
		this.filterService.setInModal(this.props.inModal);

		if (this.props.filterProps) {
			this.filterService.initFilter(this.props.filterProps);
		}

		await super.onBeforeMountAsync();
	}

	protected onMounted () {
		super.onMounted();
		this.registerBusEvent('added', async () => {
			if (!this._modal) {
				this.filterService.connect(this);
				this.filterService.setInModal(false);
				this.refreshListAsync();
			}
		});
		this.listPrivateState.isMounted = true;
	}

	protected onUnmounted () {
		this.setFilterServiceAdditionalDatas();
		super.onUnmounted();
	}

	setFilterServiceAdditionalDatas () {
		if (this.props.inModal) {
			this.filterService.setInModal(true);
			this.filterService.setOrderEntity(this.props.order);
			if (this.ui.onLayoutNavBottom)
				Bus.emit('OrderModalCandidateSetupService:close-filter');
		} else {
			this.filterService.setInModal(false);
			this.filterService.setOrderEntity(undefined);
		}
	}

	openModalAddCandidate () {
		if (this.props.order)
			this.candidateService.addCandidatesAsync(this.props.order);
	}

	addCandidate (candidates: Armado.TempWorkerListItemEntityType[]) {
		this._modal?.trigger('add-candidates', candidates);
	}

	async removeCandidateFromGroupAsync (talents: Armado.TempWorkerListItemEntityType[]) {
		this._modal?.trigger('remove-talent', talents);
	}

	async removeCandidateFromOrderAsync () {
		if (this.props.order?.id)
			await this.orderService.removeCandidateFromOrderAsync(this.props.order.id, this.listState.selected.mapKey('user.id'));

		this.listState.selected = [];
		this.refreshListAsync();
	}

	openModalCandidateFilter () {
		Bus.emit('OrderModalCandidateSetupService:open-filter');
	}
}
